// ============================================================
// ALBATROS — PromoCodeModal.tsx
// Canje del "Codigo Descuento": el cajero teclea el codigo que trae
// el cliente y, si es valido, se aplica como promocion de la venta.
// Portado del TPV de referencia (tpv-albatross-4).
// ============================================================

import { CheckCircle2, Loader2, Tag, X, XCircle } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { CODE_PROMO, REDEEMABLE_CODES } from '../../mocks/PromoCodes.mocks';
import { PromoCodeType } from '../../types/promo.types';
import './PromoCodeModal.css';

/** Teclado tactil: tres columnas, con CLR y retroceso en la ultima fila. */
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'CLR', '0', '⌫'];

/** Milisegundos de "verificacion" simulada, para que se vea que comprueba. */
const CHECK_MS = 800;
/** Lo que se deja leer el mensaje de exito antes de aplicar y cerrar. */
const APPLY_MS = 1500;

interface PromoCodeModalProps {
  onClose: () => void;
  onConfirm: (promo: PromoCodeType) => void;
}

export function PromoCodeModal({ onClose, onConfirm }: PromoCodeModalProps) {
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);
  const timers = useRef<number[]>([]);

  /** Mientras valida o ya ha acertado, el formulario queda bloqueado. */
  const isLocked = isValidating || !!successMsg;

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
    // Cerrar a media validacion dejaria los setTimeout escribiendo estado
    // sobre un componente ya desmontado.
    return () => { timers.current.forEach(window.clearTimeout); };
  }, []);

  const later = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  };

  const handleValidate = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isLocked || !code.trim()) return;

    setIsValidating(true);
    setErrorMsg('');
    setSuccessMsg('');

    later(() => {
      const upper = code.trim().toUpperCase();
      let matched = REDEEMABLE_CODES.find(c => c.code === upper);

      // Cualquier codigo numerico de 3 o mas digitos vale como 12%: es la
      // regla de las campañas que se reparten impresas con un numero suelto.
      if (!matched && /^\d{3,}$/.test(upper)) {
        matched = { code: upper, discountPercent: 12, description: `Descuento Código Numérico POS (${upper})` };
      }

      setIsValidating(false);

      if (!matched) {
        setErrorMsg('El código introducido no es válido o está caducado.');
        return;
      }

      const found = matched;
      setSuccessMsg(`¡Código "${found.code}" aplicado con éxito! (${found.discountPercent}% Descuento)`);
      later(() => onConfirm({
        code: CODE_PROMO,
        label: `${found.description} [${found.code}]`,
        discountPercent: found.discountPercent
      }), APPLY_MS);
    }, CHECK_MS);
  };

  const handleKey = (key: string) => {
    if (isLocked) return;
    if (key === 'CLR') setCode('');
    else if (key === '⌫') setCode(prev => prev.slice(0, -1));
    else setCode(prev => prev + key);
    setErrorMsg('');
    if (inputRef.current) inputRef.current.focus();
  };

  const inputState = errorMsg ? ' pcm-input--error' : successMsg ? ' pcm-input--ok' : '';

  // ── Render ───────────────────────────────────────────────

  return (
    <div className="pcm-root">
      <div className="pcm-backdrop" onClick={isLocked ? undefined : onClose} />
      <div className="pcm-card">

        <div className="pcm-ribbon">
          <Tag size={24} className="pcm-ribbon-icon" />
          <span className="pcm-ribbon-text">Canjear Código de Descuento</span>
          <button onClick={onClose} className="pcm-close" title="Cerrar" disabled={isLocked}>
            <X size={24} />
          </button>
        </div>

        <form className="pcm-body" onSubmit={handleValidate}>
          <p className="pcm-label">Introduzca un código de promoción activo para esta venta</p>

          <div className="pcm-input-wrap">
            <Tag size={20} className="pcm-input-icon" />
            <input
              ref={inputRef}
              type="text"
              value={code}
              onChange={(e) => { setCode(e.target.value); setErrorMsg(''); setSuccessMsg(''); }}
              disabled={isLocked}
              placeholder="Escriba código aquí, ej. LECTOR12"
              autoComplete="off"
              className={`pcm-input${inputState}`}
            />
            {code && !isLocked && (
              <button type="button" onClick={() => setCode('')} className="pcm-input-clear">
                LIMPIAR
              </button>
            )}
          </div>

          <div className="pcm-keypad">
            {KEYS.map(key => (
              <button
                key={key}
                type="button"
                onClick={() => handleKey(key)}
                disabled={isLocked}
                className={`pcm-key${key === 'CLR' ? ' pcm-key--clear' : key === '⌫' ? ' pcm-key--back' : ''}`}
              >
                {key}
              </button>
            ))}
          </div>

          {errorMsg && (
            <div className="pcm-msg pcm-msg--error">
              <XCircle size={16} className="pcm-msg-icon" />
              <span>{errorMsg}</span>
            </div>
          )}

          {successMsg && (
            <div className="pcm-msg pcm-msg--ok">
              <CheckCircle2 size={20} className="pcm-msg-icon" />
              <div className="pcm-msg-text">
                <span className="pcm-msg-title">{successMsg}</span>
                <span className="pcm-msg-note">Aplicando promoción, espere un momento...</span>
              </div>
            </div>
          )}

          <div className="pcm-actions">
            <button type="button" onClick={onClose} disabled={isLocked} className="pcm-btn-cancel">
              CANCELAR
            </button>
            <button type="submit" disabled={isLocked || !code.trim()} className="pcm-btn-confirm">
              {isValidating ? (
                <>
                  <Loader2 size={16} className="pcm-spin" />
                  <span>VERIFICANDO...</span>
                </>
              ) : (
                <span>VALIDAR Y APLICAR</span>
              )}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}
